import { GroupByToMap, UserHasAcl } from "../../../Utils/helpers";
import ApiClient from "../api-client";
import { assetService, EnvType } from "../services";
import { default as ISetupApiClient, default as SetupApiClient } from "../Setup/setup-api-client";
import { AclTypes } from "../Types/acl-types";
import { ChangeBroadcasterSetupResponse } from "../Types/change-broadcaster-setup-response";
import { ChangeMetadataFieldSetupResponse } from "../Types/change-metadata-field-setup-response";
import { ChangeProviderSetupResponse } from "../Types/change-provider-setup-response";
import { CopyPlatformRequest } from "../Types/copy-platform-request";
import { CreateProviderSetupFullRequest, CreateProviderSetupRequest } from "../Types/create-provider-setup-request";
import { DbAgentInfos } from "../Types/db-agent-infos";
import { DbAssetMarkerFamilyInfos } from "../Types/db-asset-marker-family-infos";
import { DbAssetMarkerTypeInfos } from "../Types/db-asset-marker-type-infos";
import { DbAssetObjectInfos } from "../Types/db-asset-object-infos";
import { DbDeliveryBroadcasterInfos } from "../Types/db-delivery-broadcaster-infos";
import { DbFilePurgeRuleInfos } from "../Types/db-file-purge-rule-infos";
import { DbFormatInfos, UploadCardFormatDto } from "../Types/db-format-infos";
import {
  AssetDeliveryPerMonthDataModel,
  AssetDeliveryPerMonthDto,
  AssetDeliveryPerMonthKeyModel,
  DataSizeDeliveredPerDayModel,
  DataSizeDeliveredPerMonthDto,
  DataSizeDeliveredPerMonthModel,
  IngestedAssetsPerMonthDataModel,
  IngestedAssetsPerMonthDto,
  IngestedAssetsPerMonthKeyModel,
  QualifiedAssetsPerDayDto,
  QualifiedAssetsPerDayItemModel,
  QualifiedAssetsPerMonthDataModel,
  QualifiedAssetsPerMonthDto,
} from "../Types/db-graphset-cache-infos";
import { DbGraphsetDeliveredItemInfos } from "../Types/db-graphset-delivered-item-infos";
import { DbGraphsetIngestedItemInfos } from "../Types/db-graphset-ingested-item-infos";
import { DbGraphsetQualifiedItemInfos } from "../Types/db-graphset-qualified-item-infos";
import { DbGraphsetTransferedItemInfos } from "../Types/db-graphset-transfered-item-infos";
import { DbLabProviderInfos, ProviderNewAssignmentDto } from "../Types/db-lab-provider-infos";
import { DbMetadataFieldInfos } from "../Types/db-metadata-field-infos";
import { DbMetadataNamingRuleInfos } from "../Types/db-metadata-naming-rule-infos";
import { DbMetadataViewInfos } from "../Types/db-metadata-view-infos";
import { DbPlatformInfos } from "../Types/db-platform-infos";
import { DbQuotaInfos } from "../Types/db-quota-infos";
import { DbStatusTransitionInfos } from "../Types/db-status-transition-infos";
import { DbStorageInfos } from "../Types/db-storage-infos";
import { DiffsDataResponse } from "../Types/diffs-data-response";
import { DumpDataRequest } from "../Types/dump-data-request";
import { FileUsage } from "../Types/file-usage";
import { FirstUserRequest } from "../Types/first-user-request";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetAgentsEnumsResponse } from "../Types/get-agents-enums-response";
import { GetAllMetadataViewInfoResponse } from "../Types/get-all-metadata-view-info-response";
import { GetAllProvidersResponse } from "../Types/get-all-providers-response";
import { GetAllStoragesResponse } from "../Types/get-all-storages-response";
import { GetBroadcasterEnumListsResponse } from "../Types/get-broadcaster-enum-lists-response";
import { GetCulturesResponse } from "../Types/get-cultures-response";
import { GetFileEnumListsResponse } from "../Types/get-file-enum-lists-response";
import { GetFileExtensionsListResponse } from "../Types/get-file-extensions-list-response";
import { GetFilePurgeRulesEnumsResponse } from "../Types/get-file-purge-rules-enums-response";
import { GetFilePurgeRulesResponse } from "../Types/get-file-purge-rules-response";
import { GetFormatsEnumsResponse } from "../Types/get-formats-enums-response";
import { GetFormatsResponse } from "../Types/get-formats-response";
import { GetGraphsetDataResponse } from "../Types/get-graphset-data-response";
import { GetJobsEnumsResponse } from "../Types/get-jobs-enums-response";
import { GetMarkerLanguagesResponse } from "../Types/get-marker-languages-response";
import { GetMarkerTranslationConfigResponse } from "../Types/get-marker-translation-config-response";
import { GetMarkerTypesByFamilyNameResponse } from "../Types/get-marker-types-by-family-name-response";
import { GetMetadataFieldEnumListsResponse } from "../Types/get-metadata-field-enum-lists-response";
import { GetMetadataFieldsListResponse } from "../Types/get-metadata-fields-list-response";
import { GetMetadataFieldsResponse } from "../Types/get-metadata-fields-response";
import { GetMetadataViewEnumsResponse } from "../Types/get-metadata-view-enums-response";
import { GetNamingRulesResponse } from "../Types/get-naming-rules-response";
import { GetPlatformInfosEnumsResponse } from "../Types/get-platform-infos-enums-response";
import { GetPlatformInfosResponse } from "../Types/get-platform-infos-response";
import { GetPlatformYtChannelsResponse } from "../Types/get-platform-yb-channels-response";
import { GetProviderEnumListsResponse } from "../Types/get-provider-enum-lists-response";
import { GetQuotaResponse } from "../Types/get-quota-response";
import { GetQuotasListResponse } from "../Types/get-quotas-list-response";
import { GetStatusTransitionsResponse } from "../Types/get-status-transitions-response";
import { GetStorageEnumListsResponse } from "../Types/get-storage-enum-lists-response";
import { GetStorageResponse } from "../Types/get-storage-response";
import { GetSuperUserEnumsResponse } from "../Types/get-super-user-enums-response";
import { GetWorkflowEnumsResponse } from "../Types/get-workflow-enums-response";
import { GraphsetType } from "../Types/graphset-type";
import { ListAgentsResponse } from "../Types/list-agents-response";
import { ListPlatformsResponse } from "../Types/list-platforms-response";
import { ObjectLayer } from "../Types/object-layer";
import { UpdateNamingRuleResponse } from "../Types/update-naming-rule-response";
import { YoutubeChannel } from "../Types/youtube-channel";

export default class SetupService {
  setupApiClient: ISetupApiClient;

  constructor(envType: EnvType) {
    this.setupApiClient = new SetupApiClient(new ApiClient(envType), envType);
  }

  async GetQualifiedAssetsPerMonth(): Promise<QualifiedAssetsPerMonthDto> {
    const graphData = (await this.GetGraphsetData(GraphsetType.QualifiedAssetsPerDay)).data?.data;

    if (!graphData || !graphData.months)
      return {
        months: [],
        sums: {},
      };
    let months: QualifiedAssetsPerMonthDataModel[] = [];
    let sums: Record<string, number> = {};

    for (let i = 0; i < graphData.months.length; i++) {
      const month = graphData.months[i];
      const dayDto: QualifiedAssetsPerDayDto = {
        data: [],
        keys: [],
      };

      if (month.days)
        for (let j = 0; j < month.days.length; j++) {
          const day = month.days[j];
          const colors = ["#ffc107", "#009688", "#9C27B0", "#2196f3", "#607D8B", "#000000", "#795548", "#f44336"];

          const groupedItems = GroupByToMap(day.items as DbGraphsetQualifiedItemInfos[], (x) => x.assetType);
          const dataModel: QualifiedAssetsPerDayItemModel = {
            name: day.name || "N/A",
          };
          let k = 0;
          groupedItems.forEach((value, key) => {
            if (!dayDto.keys.flatMap((k) => k.key).includes(key)) dayDto.keys.push({ key: key, color: colors[k] });

            dataModel[key] = value.length;

            if (k === colors.length - 1) k = 0;
            else ++k;
          });
          dayDto.data.push(dataModel);
        }
      const monthValue = month.days?.flatMap((x) => x.items).length || 0;
      sums[month.name || "N/A"] = monthValue;
      months.push({
        month: month.name || "N/A",
        sum: monthValue,
        dayDto: dayDto, // TODO,
      });
    }

    return { months, sums };
  }

  async GetGrapDeliveredPerMonth(): Promise<AssetDeliveryPerMonthDto | undefined> {
    const graphData = (await this.GetGraphsetData(GraphsetType.AssetsDeliveryPerMonth)).data?.data;

    if (!graphData || !graphData.months) return undefined;

    let data: AssetDeliveryPerMonthDataModel[] = [];
    let keys: AssetDeliveryPerMonthKeyModel[] = [];
    let j = 0;
    let sums: Record<string, number> = {};

    for (let i = 0; i < graphData.months.length; i++) {
      const month = graphData.months[i];
      let dataModel: AssetDeliveryPerMonthDataModel = {
        month: month.name || "N/A",
      };
      sums[month.name || "N/A"] = 0;

      if (month.days) {
        const groupedItems = GroupByToMap(
          month.days.flatMap((d) => d.items) as DbGraphsetDeliveredItemInfos[],
          (x) => x.productType
        );
        const colors = [
          "#7f0000",
          "#006400",
          "#008b8b",
          "#000080",
          "#9acd32",
          "#ff0000",
          "#ff8c00",
          "#ffd700",
          "#00ff00",
          "#00fa9a",
          "#4169e1",
          "#e9967a",
          "#00ffff",
          "#00bfff",
          "#0000ff",
          "#d8bfd8",
          "#ff00ff",
          "#eee8aa",
          "#ff1493",
          "#ee82ee",
        ];

        groupedItems.forEach((value, key) => {
          if (!keys.flatMap((k) => k.key).includes(key)) {
            keys.push({ key: key, color: colors[j] });
            if (j === colors.length - 1) j = 0;
            else j++;
          }

          dataModel[key] = value.length;
          sums[month.name || "N/A"] += value.length;
        });
      }
      data.push(dataModel);
    }

    return {
      data: data,
      keys: keys,
      sums: sums,
    };
  }

  async GetGraphDataIngestedAssetsPerMonth(): Promise<IngestedAssetsPerMonthDto | undefined> {
    const graphData = (await this.GetGraphsetData(GraphsetType.IngestedAssetsPerMonth)).data?.data;
    if (!graphData || !graphData.months) return undefined;

    let output: IngestedAssetsPerMonthDataModel[] = [];
    let keys: IngestedAssetsPerMonthKeyModel[] = [];
    let sums: Record<string, number> = {};

    for (let i = 0; i < graphData.months.length; i++) {
      const month = graphData.months[i];
      let dataModel: IngestedAssetsPerMonthDataModel = {
        month: month.name || "N/A",
      };
      sums[month.name || "N/A"] = 0;

      if (month.days) {
        const groupedItems = GroupByToMap(
          month.days.flatMap((d) => d.items) as DbGraphsetIngestedItemInfos[],
          (x) => x.assetClass
        );
        groupedItems.forEach((value, key) => {
          if (!keys.flatMap((k) => k.key).includes(key))
            keys.push({
              key: key,
              color: key === "Mezz" ? "#7339AC" : key === "Raw" ? "#7E7E8D" : key === "Dcp" ? "#AC7048" : "#208A73",
            });

          dataModel[key] = value.length;
          sums[month.name || "N/A"] += value.length;
        });
      }
      output.push(dataModel);
    }

    return {
      data: output,
      keys: keys,
      sums: sums,
    };
  }

  async GetGraphDataSizeDeliveredData(): Promise<DataSizeDeliveredPerMonthDto> {
    const graphData = (await this.GetGraphsetData(GraphsetType.DataSizeDelivered)).data?.data;
    if (!graphData || !graphData.months)
      return {
        months: [],
        sums: {},
      };

    let output: DataSizeDeliveredPerMonthModel[] = [];
    let sums: Record<string, number> = {};

    for (let i = 0; i < graphData.months.length; i++) {
      const month = graphData.months[i];
      let monthValue = 0;
      let daysOfMonth: DataSizeDeliveredPerDayModel[] = [];

      if (month.days)
        for (let j = 0; j < month.days.length; j++) {
          const day = month.days[j];
          if (!day.items) continue;
          const daySizeSum = (day.items as DbGraphsetTransferedItemInfos[]).reduce((a, b) => a + b.value, 0);
          daysOfMonth.push({
            name: day.name || "N/A",
            value: daySizeSum / 1024 / 1024 / 1024 / 1024,
          });
          monthValue += daySizeSum;
        }
      monthValue = monthValue / 1024 / 1024 / 1024 / 1024;
      sums[month.name || "N/A"] = Math.round((monthValue + Number.EPSILON) * 100) / 100; // Round at 2nd decimal
      output.push({ month: month.name || "N/A", value: monthValue, days: daysOfMonth });
    }

    return {
      months: output,
      sums,
    };
  }
  async GetProvidersDto(): Promise<ProviderNewAssignmentDto[]> {
    const providersAndStorage = (await this.setupApiClient.GetAllProviders()).providersAndStorages;

    if (!providersAndStorage?.length) return [];

    const providers = providersAndStorage.filter((x) => x.provider !== null).flatMap((x) => x.provider!);

    return providers.map((provider) => ({
      id: provider.id,
      name: provider.labName || "-",
    }));
  }

  async GetFormatsForAsperaAnnexUpload(assetUploadableFormatsIds: number[]): Promise<UploadCardFormatDto[]> {
    if (assetUploadableFormatsIds.length === 0) return [];
    const fetchedFormats = (await this.setupApiClient.GetFormats()).formats;

    if (!fetchedFormats) return [];

    const formatsToKeep = fetchedFormats.filter((x) => assetUploadableFormatsIds.includes(x.id));

    return this.MapDbFormatInfosToUploadCardFormatDto(formatsToKeep);
  }

  async GetFormatsByLayerAndTypeFromObject(object: DbAssetObjectInfos): Promise<UploadCardFormatDto[]> {
    const assetLayer = object.layer;
    const assetType = object.type;
    if (!assetLayer || !assetType) return [];
    let formats = (await this.GetFormats()).formats || [];

    // Filter on asset layer
    formats = formats.filter(
      (x) =>
        x.objectLayers?.includes(assetLayer) &&
        x.usage === FileUsage.UserUpload &&
        x.isUploadable &&
        x.defaultStorageId !== null
    );

    if (assetLayer === ObjectLayer.Asset)
      formats = formats.filter(
        (x) => x.objectTypes === null || x.objectTypes.length === 0 || x.objectTypes.includes(assetType)
      );
    return this.MapDbFormatInfosToUploadCardFormatDto(formats);
  }

  async GetFormatsByLayerAndType(objectId: number): Promise<UploadCardFormatDto[]> {
    const asset = await assetService.GetObjectLightById(objectId, 1);
    if (!asset.graph) return [];
    return await this.GetFormatsByLayerAndTypeFromObject(asset.graph);
  }

  MapDbFormatInfosToUploadCardFormatDto(formatsToMap: DbFormatInfos[]) {
    const output: UploadCardFormatDto[] = [];

    for (let index = 0; index < formatsToMap.length; index++) {
      const format = formatsToMap[index];
      if (!format.name || !format.maxFileSizeMB || !format.fileExtensions) continue;

      const acceptsFiles: { [key: string]: string[] } = { "application/octet-stream": [] };
      format.fileExtensions.forEach((extension) => {
        // TODO : map mime types in db, can be useful, see : https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
        acceptsFiles["application/octet-stream"].push(extension.fileExtension!);
      });

      output.push({
        id: format.id,
        name: format.name,
        objectType: format.objectType,
        description: format.description || undefined,
        maxFileSizeMb: format.maxFileSizeMB,
        acceptsFiles,
        isUploaded: false,
        isUserDataMandatory: format.isUserDataMandatory || false,
        userHasRighToDelete: format.isDeletable! && format.isDeletable && UserHasAcl(AclTypes.aclMediaFileDelete),
        linkedType: format.linkedType,
      });
    }
    return output;
  }
  //------Not used------

  async GetMetadataFieldsByBroadcasterId(broadcasterId: number): Promise<GetMetadataFieldsResponse> {
    return await this.setupApiClient.GetMetadataFieldsByBroadcasterId(broadcasterId);
  }
  async GetMetadataFieldsByBroadcasterIdAndCultureId(
    broadcasterId: number,
    countryId: number
  ): Promise<GetMetadataFieldsResponse> {
    return await this.setupApiClient.GetMetadataFieldsByBroadcasterIdAndCultureId(broadcasterId, countryId);
  }
  async GetMetadataFieldsByBroadcasterIdAndCultureCode(
    broadcasterId: number,
    countryCode: string
  ): Promise<GetMetadataFieldsResponse> {
    return await this.setupApiClient.GetMetadataFieldsByBroadcasterIdAndCultureCode(broadcasterId, countryCode);
  }
  async GetMetadataFieldEnums(): Promise<GetMetadataFieldEnumListsResponse> {
    return await this.setupApiClient.GetMetadataFieldEnums();
  }
  async CreateMetadataField(field: DbMetadataFieldInfos): Promise<ChangeMetadataFieldSetupResponse> {
    return await this.setupApiClient.CreateMetadataField(field);
  }
  async UpdateMetadataField(field: DbMetadataFieldInfos): Promise<ChangeMetadataFieldSetupResponse> {
    return await this.setupApiClient.UpdateMetadataField(field);
  }
  async DeleteMetadataField(id: number): Promise<GenericResponseStd> {
    return await this.setupApiClient.DeleteMetadataField(id);
  }
  async GetAllMetadataViewsInfo(): Promise<GetAllMetadataViewInfoResponse> {
    return await this.setupApiClient.GetAllMetadataViewsInfo();
  }
  async GetAllMetadataFields(): Promise<GetMetadataFieldsListResponse> {
    return await this.setupApiClient.GetAllMetadataFields();
  }
  async GetMetadataViewEnumsInfo(): Promise<GetMetadataViewEnumsResponse> {
    return await this.setupApiClient.GetMetadataViewEnumsInfo();
  }
  async CreateMetadataView(view: DbMetadataViewInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.CreateMetadataView(view);
  }
  async AddFolderFilterToMetadataView(id: number, folderId: number): Promise<GenericResponseStd> {
    return await this.setupApiClient.AddFolderFilterToMetadataView(id, folderId);
  }
  async UpdateMetadataView(view: DbMetadataViewInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.UpdateMetadataView(view);
  }
  async DeleteMetadataView(id: number): Promise<GenericResponseStd> {
    return await this.setupApiClient.DeleteMetadataView(id);
  }
  async GetBroadcasterEnums(): Promise<GetBroadcasterEnumListsResponse> {
    return await this.setupApiClient.GetBroadcasterEnums();
  }
  async CreateBroadcaster(broadcaster: DbDeliveryBroadcasterInfos): Promise<ChangeBroadcasterSetupResponse> {
    return await this.setupApiClient.CreateBroadcaster(broadcaster);
  }
  async UpdateBroadcaster(broadcaster: DbDeliveryBroadcasterInfos): Promise<ChangeBroadcasterSetupResponse> {
    return await this.setupApiClient.UpdateBroadcaster(broadcaster);
  }
  async DeleteBroadcaster(id: number): Promise<GenericResponseStd> {
    return await this.setupApiClient.DeleteBroadcaster(id);
  }
  async GetAllCultures(): Promise<GetCulturesResponse> {
    return await this.setupApiClient.GetAllCultures();
  }
  async GetGraphsetData(graphsetType: GraphsetType): Promise<GetGraphsetDataResponse> {
    return await this.setupApiClient.GetGraphsetData(graphsetType);
  }
  async GetProviderEnums(): Promise<GetProviderEnumListsResponse> {
    return await this.setupApiClient.GetProviderEnums();
  }
  async GetAllProviders(): Promise<GetAllProvidersResponse> {
    return await this.setupApiClient.GetAllProviders();
  }
  async CreateProvider(request: CreateProviderSetupRequest): Promise<ChangeProviderSetupResponse> {
    return await this.setupApiClient.CreateProvider(request);
  }
  async CreateProviderFull(request: CreateProviderSetupFullRequest): Promise<ChangeProviderSetupResponse> {
    return await this.setupApiClient.CreateProviderFull(request);
  }
  async UpdateProvider(request: DbLabProviderInfos): Promise<ChangeProviderSetupResponse> {
    return await this.setupApiClient.UpdateProvider(request);
  }
  async DeleteProvider(providerId: number): Promise<GenericResponseStd> {
    return await this.setupApiClient.DeleteProvider(providerId);
  }
  async GetSuperUserEnums(): Promise<GetSuperUserEnumsResponse> {
    return await this.setupApiClient.GetSuperUserEnums();
  }
  async DumpData(request: DumpDataRequest): Promise<GenericResponseStd> {
    return await this.setupApiClient.DumpData(request);
  }
  async Diffs(request: DumpDataRequest): Promise<DiffsDataResponse> {
    return await this.setupApiClient.Diffs(request);
  }
  async CreateFirstUser(request: FirstUserRequest): Promise<GenericResponseStd> {
    return await this.setupApiClient.CreateFirstUser(request);
  }
  async ListAgents(): Promise<ListAgentsResponse> {
    return await this.setupApiClient.ListAgents();
  }
  async UpdateAgent(request: DbAgentInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.UpdateAgent(request);
  }
  async GetAgentEnums(): Promise<GetAgentsEnumsResponse> {
    return await this.setupApiClient.GetAgentEnums();
  }
  async ListPlatforms(): Promise<ListPlatformsResponse> {
    return await this.setupApiClient.ListPlatforms();
  }
  async GetFormats(): Promise<GetFormatsResponse> {
    return await this.setupApiClient.GetFormats();
  }
  async GetFormatEnums(): Promise<GetFormatsEnumsResponse> {
    return await this.setupApiClient.GetFormatEnums();
  }
  async CreateFormat(request: DbFormatInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.CreateFormat(request);
  }
  async UpdateFormat(request: DbFormatInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.UpdateFormat(request);
  }
  async DeleteFormat(id: number): Promise<GenericResponseStd> {
    return await this.setupApiClient.DeleteFormat(id);
  }
  async CreateFilePurgeRule(rule: DbFilePurgeRuleInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.CreateFilePurgeRule(rule);
  }
  async UpdateFilePurgeRule(rule: DbFilePurgeRuleInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.UpdateFilePurgeRule(rule);
  }
  async DeleteFilePurgeRule(id: number): Promise<GenericResponseStd> {
    return await this.setupApiClient.DeleteFilePurgeRule(id);
  }
  async GetFilePurgeRules(): Promise<GetFilePurgeRulesResponse> {
    return await this.setupApiClient.GetFilePurgeRules();
  }
  async GetFilePurgeRulesEnums(): Promise<GetFilePurgeRulesEnumsResponse> {
    return await this.setupApiClient.GetFilePurgeRulesEnums();
  }
  async GetJobsEnums(): Promise<GetJobsEnumsResponse> {
    return await this.setupApiClient.GetJobsEnums();
  }
  async GetFileEnums(): Promise<GetFileEnumListsResponse> {
    return await this.setupApiClient.GetFileEnums();
  }
  async GetFileExtensions(): Promise<GetFileExtensionsListResponse> {
    return await this.setupApiClient.GetFileExtensions();
  }
  async GetStoragesEnums(): Promise<GetStorageEnumListsResponse> {
    return await this.setupApiClient.GetStoragesEnums();
  }
  async GetAllStorages(): Promise<GetAllStoragesResponse> {
    return await this.setupApiClient.GetAllStorages();
  }
  async GetExternalStorages(): Promise<GetAllStoragesResponse> {
    return await this.setupApiClient.GetExternalStorages();
  }
  async GetStorage(storageId: number): Promise<GetStorageResponse> {
    return await this.setupApiClient.GetStorage(storageId);
  }
  async CreateStorage(storage: DbStorageInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.CreateStorage(storage);
  }
  async UpdateStorage(storage: DbStorageInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.UpdateStorage(storage);
  }
  async GetStatusTransitions(): Promise<GetStatusTransitionsResponse> {
    return await this.setupApiClient.GetStatusTransitions();
  }
  async UpdateStatusTransitions(request: DbStatusTransitionInfos[]): Promise<GenericResponseStd> {
    return await this.setupApiClient.UpdateStatusTransitions(request);
  }
  async GetWorkflowEnums(): Promise<GetWorkflowEnumsResponse> {
    return await this.setupApiClient.GetWorkflowEnums();
  }
  async GetPlatformInfosEnums(): Promise<GetPlatformInfosEnumsResponse> {
    return await this.setupApiClient.GetPlatformInfosEnums();
  }
  async ReloadConfig(): Promise<GenericResponseStd> {
    return await this.setupApiClient.ReloadConfig();
  }
  async GetPlatformInfos(): Promise<GetPlatformInfosResponse> {
    return await this.setupApiClient.GetPlatformInfos();
  }
  async GetYtChannels(): Promise<GetPlatformYtChannelsResponse> {
    return await this.setupApiClient.GetYtChannels();
  }
  async UpdatePlatformInfos(platform: DbPlatformInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.UpdatePlatformInfos(platform);
  }
  async UpdateYoutubeChannelsInfos(ybChannels: YoutubeChannel[]): Promise<GenericResponseStd> {
    return await this.setupApiClient.UpdateYoutubeChannelsInfos(ybChannels);
  }
  async CopyPlatform(request: CopyPlatformRequest): Promise<GenericResponseStd> {
    return await this.setupApiClient.CopyPlatform(request);
  }
  async GetMarkerLanguages(): Promise<GetMarkerLanguagesResponse> {
    return await this.setupApiClient.GetMarkerLanguages();
  }
  async GetMarkerTranslationConfig(): Promise<GetMarkerTranslationConfigResponse> {
    return await this.setupApiClient.GetMarkerTranslationConfig();
  }
  async GetMarkerTypesByFamilyName(familyName: string): Promise<GetMarkerTypesByFamilyNameResponse> {
    return await this.setupApiClient.GetMarkerTypesByFamilyName(familyName);
  }
  async CreateMarkerFamily(request: DbAssetMarkerFamilyInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.CreateMarkerFamily(request);
  }
  async UpdateMarkerFamily(request: DbAssetMarkerFamilyInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.UpdateMarkerFamily(request);
  }
  async DeleteMarkerFamily(id: number): Promise<GenericResponseStd> {
    return await this.setupApiClient.DeleteMarkerFamily(id);
  }
  async CreateMarkerType(request: DbAssetMarkerTypeInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.CreateMarkerType(request);
  }
  async UpdateMarkerType(request: DbAssetMarkerTypeInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.UpdateMarkerType(request);
  }
  async DeleteMarkerType(id: number): Promise<GenericResponseStd> {
    return await this.setupApiClient.DeleteMarkerType(id);
  }
  async GetAllQuotasList(): Promise<GetQuotasListResponse> {
    return await this.setupApiClient.GetAllQuotasList();
  }
  async CreateQuota(quota: DbQuotaInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.CreateQuota(quota);
  }
  async UpdateQuota(request: DbQuotaInfos): Promise<GenericResponseStd> {
    return await this.setupApiClient.UpdateQuota(request);
  }
  async DeleteQuota(id: number): Promise<GenericResponseStd> {
    return await this.setupApiClient.DeleteQuota(id);
  }
  async GetQuota(id: number): Promise<GetQuotaResponse> {
    return await this.setupApiClient.GetQuota(id);
  }
  async GetNamingRules(): Promise<GetNamingRulesResponse> {
    return await this.setupApiClient.GetNamingRules();
  }
  async UpdateNamingRule(namingRule: DbMetadataNamingRuleInfos): Promise<UpdateNamingRuleResponse> {
    return await this.setupApiClient.UpdateNamingRule(namingRule);
  }
  async CreateNamingRule(namingRule: DbMetadataNamingRuleInfos): Promise<UpdateNamingRuleResponse> {
    return await this.setupApiClient.CreateNamingRule(namingRule);
  }
  async DeleteNamingRule(id: number): Promise<GetNamingRulesResponse> {
    return await this.setupApiClient.DeleteNamingRule(id);
  }

  //------Utils------
  GetServiceUrl(): string {
    return this.setupApiClient.GetApiUrl();
  }
}
